import _ from 'lodash';
import {observable, computed, action, makeObservable} from 'mobx';
import {DomainObject} from 'shared/DomainObject';
import {fullName} from 'shared/tools';
import BankAccount from 'main/stores/bank_account/BankAccount';
import AttachedDocument from 'shared/stores/documents/AttachedDocument';
import EmployeeCustomField from 'main/stores/employee_custom_fields/EmployeeCustomField';
import {EmployeeBenefitEnrollment, EmployeeInsuranceNumber} from 'main/stores/benefits';
import {TimeOffPolicy} from 'main/stores/time_off';
import Location from 'main/stores/locations/Location';
import Department from 'main/stores/departments/Department';
import {TaskTemplate} from 'main/stores/tasks';
import {CompanyDocument} from 'main/stores/documents';
import EmployeeOnboard from './EmployeeOnboard';

class Employee extends DomainObject {
  firstName;
  lastName;
  employeeNumber;
  jobTitle;
  preferredName;
  preferredLastName;
  status;
  location;
  department;
  user;
  avatarColour;
  avatarInitials;
  employmentType;

  middleName;
  workPhone;
  workPhoneExtension;
  personalPhone;
  workEmail;
  personalEmail;
  gender;
  customGender;
  birthDate;
  homeAddress1;
  homeAddress2;
  postalCode;
  city;
  countryCode;
  regionCode;

  sin;
  sinExpiryDate;
  bankAccount;

  ec1Name;
  ec1Relationship;
  ec1Phone;
  ec2Name;
  ec2Relationship;
  ec2Phone;
  employeeCustomFields;

  invitationEmail;

  avatar;

  startDate;
  payRate;
  payRateUnit;
  payRateCurrency;
  payFrequency;
  hoursPerWeek;
  commission;
  commissionStructure;
  annualizedCommission;
  bonus;
  bonusStructure;

  employeeBenefitEnrollments;
  employeeInsuranceNumbers;
  collectTax;
  taxLocale;
  collectPayroll;
  collectSin;
  payrollEnabled;
  nmbrPayrollEnabled;
  nmbrPayScheduleId;
  existsInNmbr;

  timeOffPolicy;
  timeTrackingPolicy;

  employmentStatus;
  onboardingStatus;

  onboard;
  onboardingTasks;
  onboardingDocuments;
  finishConfig = false;

  errorsFromLastSync = {};

  _relations = {
    location: Location,
    department: Department,
    bankAccount: BankAccount,
    employeeCustomFields: [EmployeeCustomField],
    avatar: AttachedDocument,
    timeOffPolicy: TimeOffPolicy,
    onboard: EmployeeOnboard,
    onboardingTasks: [TaskTemplate],
    onboardingDocuments: [CompanyDocument],
    _dates: ['sinExpiryDate', 'birthDate', 'startDate'],
    employeeBenefitEnrollments: [EmployeeBenefitEnrollment],
    employeeInsuranceNumbers: [EmployeeInsuranceNumber]
  };

  constructor(other) {
    super();

    makeObservable(this, {
      firstName: observable,
      lastName: observable,
      employeeNumber: observable,
      jobTitle: observable,
      preferredName: observable,
      preferredLastName: observable,
      status: observable,
      location: observable,
      department: observable,
      user: observable,
      avatarColour: observable,
      avatarInitials: observable,
      employmentType: observable,
      middleName: observable,
      workPhone: observable,
      workPhoneExtension: observable,
      personalPhone: observable,
      workEmail: observable,
      personalEmail: observable,
      gender: observable,
      customGender: observable,
      birthDate: observable,
      homeAddress1: observable,
      homeAddress2: observable,
      postalCode: observable,
      city: observable,
      countryCode: observable,
      regionCode: observable,
      sin: observable,
      sinExpiryDate: observable,
      bankAccount: observable,
      ec1Name: observable,
      ec1Relationship: observable,
      ec1Phone: observable,
      ec2Name: observable,
      ec2Relationship: observable,
      ec2Phone: observable,
      employeeCustomFields: observable,
      invitationEmail: observable,
      avatar: observable,
      startDate: observable,
      payRate: observable,
      payRateUnit: observable,
      payRateCurrency: observable,
      payFrequency: observable,
      hoursPerWeek: observable,
      commission: observable,
      commissionStructure: observable,
      annualizedCommission: observable,
      bonus: observable,
      bonusStructure: observable,
      employeeBenefitEnrollments: observable,
      employeeInsuranceNumbers: observable,
      collectTax: observable,
      taxLocale: observable,
      collectPayroll: observable,
      collectSin: observable,
      payrollEnabled: observable,
      nmbrPayrollEnabled: observable,
      nmbrPayScheduleId: observable,
      existsInNmbr: observable,
      timeOffPolicy: observable,
      timeTrackingPolicy: observable,
      employmentStatus: observable,
      onboardingStatus: observable,
      onboard: observable,
      onboardingTasks: observable,
      onboardingDocuments: observable,
      finishConfig: observable,
      errorsFromLastSync: observable,
      updateCountry: action,
      statusTags: computed,
      active: computed,
      terminated: computed,
      onLeave: computed,
      draft: computed,
      onboardingComplete: computed,
      onboardingInviteSkipped: computed,
      name: computed,
      urlSlug: computed,
      hasTimeOff: computed,
      firstNameDisplay: computed,
      lastNameDisplay: computed,
      employeePersonalCustomFields: computed,
      employeeJobCustomFields: computed,
      genderView: computed,
      sortedCustomFields: computed
    });

    this.merge(other);
  }

  updateCountry(countryCode) {
    if (this.countryCode !== countryCode) {
      this.regionCode = '';
    }

    this.countryCode = countryCode;
  }

  get statusTags() {
    const tags = [];

    if (this.terminated || this.onLeave) {
      tags.push(this.employmentStatus);
    }

    if ((this.onboardingStatus && !this.onboardingComplete) || this.onboardingInviteSkipped) {
      tags.push(this.onboardingStatus);
    }

    return tags;
  }

  get active() {
    return this.employmentStatus === 'active';
  }

  get terminated() {
    return this.employmentStatus === 'terminated';
  }

  get onLeave() {
    return this.employmentStatus === 'on_leave';
  }

  get draft() {
    return this.onboardingStatus === 'draft';
  }

  get onboardingComplete() {
    return this.onboardingStatus === 'complete' || this.onboardingInviteSkipped;
  }

  get onboardingInviteSkipped() {
    return this.onboardingStatus === 'invite_skipped';
  }

  get name() {
    return fullName(this);
  }

  get urlSlug() {
    return _.kebabCase(this.name);
  }

  get hasTimeOff() {
    return !!this.timeOffPolicy;
  }

  get firstNameDisplay() {
    return this.preferredName || this.firstName;
  }

  get lastNameDisplay() {
    return this.preferredLastName || this.lastName;
  }

  get employeePersonalCustomFields() {
    return _.filter(this.employeeCustomFields, {category: 'personal'});
  }

  get employeeJobCustomFields() {
    return _.filter(this.employeeCustomFields, {category: 'job'});
  }

  get genderView() {
    if (this.gender === 'other' && this.customGender) {
      return this.customGender;
    }
    if (this.gender) {
      return `models.employee.gender.${this.gender}`;
    }

    return '';
  }

  get sortedCustomFields() {
    return _.sortBy(this.employeeCustomFields, ['order']);
  }

  static defaultOrderAttribute() {
    return 'firstName';
  }
}

export default Employee;
